import { useState, useEffect, useMemo } from "react";
import {
  Card,
  Button,
  Input,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "@heroui/react";
import { Plus, Search, Filter, SortDesc, SortAsc } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { customersService } from "@/services/customersService";
import { Customer, CustomerCreate } from "@/types/customers";
import CustomersTable from "@/components/customers/CustomersTable";
import CustomerFormModal from "@/components/customers/CustomerFormModal";

const CustomersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>("last_order_date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);

      const params = {
        search: debouncedSearchTerm,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };

      const response = await customersService.getCustomers(params);

      setCustomers(response);
      setTotalCustomers(response.length);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customers when search, pagination, or sort changes
  useEffect(() => {

    fetchCustomers();
  }, [debouncedSearchTerm, page, pageSize, sortField, sortDirection]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalCustomers / pageSize);
  }, [totalCustomers, pageSize]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Default to descending for a new field
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const handleViewCustomer = (customerId: number) => {
    navigate(`/customers/${customerId}`);
  };

  const handleCreateCustomer = () => {
    setIsCreateModalOpen(true);
  };

  const handleCustomerCreated = async (customerData: CustomerCreate) => {
    await customersService.createCustomer(customerData);

    // Refresh the customer list
    setPage(1);
    setDebouncedSearchTerm(searchTerm);
    setIsCreateModalOpen(false);
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Customers</h1>
        <Button
          color="primary"
          startContent={<Plus size={16} />}
          onPress={handleCreateCustomer}
        >
          Add Customer
        </Button>
      </div>

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
          <div className="w-full sm:w-1/2">
            <Input
              isClearable
              placeholder="Search by name, phone, or email"
              startContent={<Search className="text-default-400" size={16} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
            />
          </div>
          <div className="flex gap-2">
            <Dropdown>
              <DropdownTrigger>
                <Button startContent={<Filter size={16} />} variant="flat">
                  Filter
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Filter options">
                <DropdownItem key="all">All Customers</DropdownItem>
                <DropdownItem key="recent">Recent Orders</DropdownItem>
                <DropdownItem key="high-value">High Value</DropdownItem>
                <DropdownItem key="inactive">Inactive</DropdownItem>
              </DropdownMenu>
            </Dropdown>

            <Dropdown>
              <DropdownTrigger>
                <Button
                  startContent={
                    sortDirection === "asc" ? (
                      <SortAsc size={16} />
                    ) : (
                      <SortDesc size={16} />
                    )
                  }
                  variant="flat"
                >
                  Sort
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Sort options">
                <DropdownItem
                  key="name"
                  startContent={
                    sortField === "first_name" &&
                    (sortDirection === "asc" ? (
                      <SortAsc size={16} />
                    ) : (
                      <SortDesc size={16} />
                    ))
                  }
                  onPress={() => handleSort("first_name")}
                >
                  Name
                </DropdownItem>
                <DropdownItem
                  key="last_order"
                  startContent={
                    sortField === "last_order_date" &&
                    (sortDirection === "asc" ? (
                      <SortAsc size={16} />
                    ) : (
                      <SortDesc size={16} />
                    ))
                  }
                  onPress={() => handleSort("last_order_date")}
                >
                  Last Order Date
                </DropdownItem>
                <DropdownItem
                  key="total_orders"
                  startContent={
                    sortField === "total_orders" &&
                    (sortDirection === "asc" ? (
                      <SortAsc size={16} />
                    ) : (
                      <SortDesc size={16} />
                    ))
                  }
                  onPress={() => handleSort("total_orders")}
                >
                  Total Orders
                </DropdownItem>
                <DropdownItem
                  key="total_spent"
                  startContent={
                    sortField === "total_spent" &&
                    (sortDirection === "asc" ? (
                      <SortAsc size={16} />
                    ) : (
                      <SortDesc size={16} />
                    ))
                  }
                  onPress={() => handleSort("total_spent")}
                >
                  Total Spent
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>

        {loading && customers.length === 0 ? (
          <div className="flex justify-center items-center py-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <CustomersTable
            customers={customers}
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            total={totalCustomers}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onView={handleViewCustomer}
            loading={loading}
            onEdit={fetchCustomers}
          />
        )}
      </Card>
      {isCreateModalOpen && (
        <CustomerFormModal
          customer={null}
          loading={false}
          onCancel={() => setIsCreateModalOpen(false)}
          onSave={handleCustomerCreated}
        />
      )}
    </div>
  );
};

export default CustomersPage;
